import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../hooks/use-toast';
import { Key, Shield, LogOut } from 'lucide-react';
import { authAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';

const AdminSettings = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isLoggingOutAll, setIsLoggingOutAll] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast({
        title: "Error",
        description: "New passwords do not match",
        variant: "destructive"
      });
      return;
    }

    if (passwordData.new_password.length < 8) {
      toast({
        title: "Error",
        description: "New password must be at least 8 characters",
        variant: "destructive"
      });
      return;
    }

    setIsChangingPassword(true);

    try {
      await authAPI.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });

      toast({
        title: "Password changed",
        description: "Your password has been updated. Please login again."
      });

      // Mark that password has been changed (for security warning)
      localStorage.setItem('passwordChanged', 'true');

      // Clear auth and redirect to login
      authAPI.clearAuth();
      navigate('/admin/login');
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to change password",
        variant: "destructive"
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleLogoutAll = async () => {
    setIsLoggingOutAll(true);
    
    try {
      await authAPI.logoutAll();
      
      toast({
        title: "Logged out",
        description: "You have been logged out from all devices"
      });

      authAPI.clearAuth();
      navigate('/admin/login');
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to logout from all devices",
        variant: "destructive"
      });
    } finally {
      setIsLoggingOutAll(false);
    }
  };

  const content = (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-light text-white mb-2">Security Settings</h1>
        <p className="text-gray-400">Manage your account security</p>
      </div>

      {/* Change Password Section */}
      <div className="bg-white/5 border border-white/10 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Key className="w-5 h-5 text-white" />
          <h2 className="text-xl font-light text-white">Change Password</h2>
        </div>
        
        <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm text-gray-400 mb-2 uppercase tracking-wider">
              Current Password
            </label>
            <Input
              type="password"
              value={passwordData.current_password}
              onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
              required
              className="w-full bg-transparent border-white/20 focus:border-white text-white"
              placeholder="Enter current password"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2 uppercase tracking-wider">
              New Password
            </label>
            <Input
              type="password"
              value={passwordData.new_password}
              onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
              required
              minLength={8}
              className="w-full bg-transparent border-white/20 focus:border-white text-white"
              placeholder="Enter new password (min 8 characters)"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2 uppercase tracking-wider">
              Confirm New Password
            </label>
            <Input
              type="password"
              value={passwordData.confirm_password}
              onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
              required
              className="w-full bg-transparent border-white/20 focus:border-white text-white"
              placeholder="Confirm new password"
            />
          </div>

          <Button
            type="submit"
            disabled={isChangingPassword}
            className="bg-white text-black hover:bg-gray-200 transition-all duration-300"
          >
            {isChangingPassword ? 'Changing...' : 'Change Password'}
          </Button>
        </form>

        <p className="text-xs text-gray-500 mt-4">
          Note: Changing your password will log you out from all devices.
        </p>
      </div>

      {/* Active Sessions Section */}
      <div className="bg-white/5 border border-white/10 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Shield className="w-5 h-5 text-white" />
          <h2 className="text-xl font-light text-white">Session Management</h2>
        </div>
        
        <p className="text-gray-400 mb-4">
          If you suspect unauthorized access to your account, you can log out from all devices.
        </p>

        <Button
          onClick={handleLogoutAll}
          disabled={isLoggingOutAll}
          variant="outline"
          className="border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300"
        >
          <LogOut className="mr-2 w-4 h-4" />
          {isLoggingOutAll ? 'Logging out...' : 'Logout from All Devices'}
        </Button>
      </div>

      {/* Security Tips */}
      <div className="bg-white/5 border border-white/10 p-6">
        <h2 className="text-xl font-light text-white mb-4">Security Tips</h2>
        <ul className="space-y-2 text-sm text-gray-400">
          <li className="flex items-start space-x-2">
            <span className="text-white">•</span>
            <span>Use a strong, unique password with at least 8 characters</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-white">•</span>
            <span>Combine uppercase, lowercase, numbers, and special characters</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-white">•</span>
            <span>Never share your login credentials with others</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-white">•</span>
            <span>Log out when using shared or public computers</span>
          </li>
        </ul>
      </div>
    </div>
  );

  return (
    <AdminLayout>
      {content}
    </AdminLayout>
  );
};

export default AdminSettings;
